<script>
  import { onMount, onDestroy } from 'svelte';
  import Footer from '../components/Footer.svelte';
  import Header from '../components/Header.svelte';
  import AccountBottomNav from '../components/AccountBottomNav.svelte';
  import AccountAuthSection from '../sections/AccountAuthSection.svelte';
  import AccountHeroSection from '../sections/AccountHeroSection.svelte';
  import AccountTicketSection from '../sections/AccountTicketSection.svelte';
  import AccountRouteSection from '../sections/AccountRouteSection.svelte';
  import AccountProfileSection from '../sections/AccountProfileSection.svelte';
  import AccountActionsSection from '../sections/AccountActionsSection.svelte';
  import AccountSupportSection from '../sections/AccountSupportSection.svelte';
  import {
    hydrate,
    step,
    profile,
    orders,
    ordersError,
    paymentPolling,
    paymentTimedOut,
    startPaymentPolling,
    stopPaymentPolling
  } from '../stores/account.js';
  import { fullNameFromProfile } from '../sections/account-profile.js';
  import { isOrderPaid } from '../sections/account-ticket.js';
  import {
    markPaymentReturn,
    readPaymentReturn,
    clearPaymentReturn,
    readOrderMarker
  } from '../utils/orderGuard.js';

  let paidFlag = false;
  // Флаг «компонент уже размонтирован». Нужен, чтобы после `await hydrate()`
  // не плодить payment-polling на уже уничтоженной странице.
  let cancelled = false;

  onMount(async () => {
    if (typeof window !== 'undefined') {
      try {
        paidFlag = new URLSearchParams(window.location.search).get('paid') === '1';
      } catch {}
    }
    await hydrate();
    if (cancelled) return;

    // Авто-polling статуса оплаты (логика без изменений: post-payment-маркер).
    const pendingPayable = $orders.filter((o) => !isOrderPaid(o) && o.payment_link);

    if (paidFlag) {
      const createdMarker = readOrderMarker();
      if (createdMarker && Number.isFinite(createdMarker.orderId)) {
        markPaymentReturn([createdMarker.orderId]);
      } else if (pendingPayable.length > 0) {
        const latest = [...pendingPayable].sort((a, b) => {
          const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
          return tb - ta;
        })[0];
        if (latest) markPaymentReturn([latest.id]);
      }
    }

    const pendingMarker = readPaymentReturn();
    if (pendingMarker) {
      const stillPendingTargets = pendingMarker.orderIds.filter((id) =>
        pendingPayable.some((o) => o.id === id)
      );
      if (stillPendingTargets.length > 0) {
        startPaymentPolling(stillPendingTargets);
      } else if ($step === 'ready' && !$ordersError) {
        clearPaymentReturn();
      }
    }
  });

  // Polling завершился — чистим маркер.
  let wasPolling = false;
  $: {
    const polling = $paymentPolling;
    if (wasPolling && !polling) {
      clearPaymentReturn();
    }
    wasPolling = polling;
  }

  onDestroy(() => {
    cancelled = true;
    stopPaymentPolling();
  });

  $: fullName = fullNameFromProfile($profile);
  $: hasPaidOrder = ($orders ?? []).some(isOrderPaid);
</script>

<div class="account-page">
  <Header variant="account" />

  <main class="account-main">
    {#if $step === 'loading'}
      <section class="account-loading">
        <span class="spinner" aria-hidden="true"></span>
        <p>Загрузка кабинета…</p>
      </section>
    {:else if $step === 'ready'}
      <div class="account-frame">
        <section class="account-grid">
          <div class="account-main-col">
            <AccountHeroSection {hasPaidOrder} userName={fullName} />

            {#if $paymentPolling}
              <div class="payment-banner pending" role="status">
                <span class="banner-spinner" aria-hidden="true"></span>
                <span>Проверяем статус оплаты…</span>
              </div>
            {:else if $paymentTimedOut}
              <div class="payment-banner timeout" role="status">
                <strong>Платёж обрабатывается.</strong>
                <span>Обычно занимает 1–2 минуты. Обновите страницу или вернитесь чуть позже — статус билета обновится автоматически.</span>
              </div>
            {/if}

            {#if $ordersError}
              <div class="orders-error" role="alert">
                <strong>{$ordersError}</strong>
              </div>
            {/if}

            <AccountTicketSection orders={$orders} />

            <AccountRouteSection />
          </div>

          <aside class="account-aside">
            <AccountProfileSection profile={$profile} />
            <AccountActionsSection />
            <AccountSupportSection />
          </aside>
        </section>
      </div>

      <AccountBottomNav />
    {:else}
      <AccountAuthSection />
    {/if}
  </main>

  <Footer />
</div>

<style>
  .account-page {
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    background:
      radial-gradient(circle at 20% 0%, rgba(231, 185, 98, 0.12), transparent 26rem),
      radial-gradient(circle at 95% 8%, rgba(255, 255, 255, 0.06), transparent 22rem),
      linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0) 320px),
      #000000;
    color: #ffffff;
  }

  .account-main {
    flex: 1 0 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    padding-top: 76px;
  }

  /* ============== Loading ============== */
  .account-loading {
    flex: 1 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 16px;
    padding: 80px 24px;
    color: rgba(255, 255, 255, 0.7);
  }

  .spinner {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 3px solid rgba(231, 185, 98, 0.25);
    border-top-color: #e7b962;
    animation: spin 0.9s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* ============== Frame + grid ============== */
  .account-frame {
    width: 100%;
    max-width: 1180px;
    margin: 0 auto;
    padding: 32px 40px 64px;
    box-sizing: border-box;
  }

  .account-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 310px;
    gap: 32px;
    align-items: start;
  }

  .account-main-col {
    display: flex;
    flex-direction: column;
    gap: 32px;
    min-width: 0;
  }

  .account-aside {
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: sticky;
    top: 104px;
    align-self: flex-start;
  }

  /* ============== Payment banners ============== */
  .payment-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 20px;
    flex-wrap: wrap;
  }

  .payment-banner.pending {
    border: 1px solid rgba(231, 185, 98, 0.3);
    background: transparent;
    color: #e7b962;
  }

  .payment-banner.timeout {
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
  }

  .payment-banner strong {
    color: #ffffff;
  }

  .banner-spinner {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid rgba(231, 185, 98, 0.25);
    border-top-color: #e7b962;
    animation: spin 0.9s linear infinite;
    flex: 0 0 auto;
  }

  .orders-error {
    padding: 12px 16px;
    border: 1px solid rgba(255, 107, 107, 0.3);
    border-radius: 12px;
    background: transparent;
    color: #ff8a8a;
    font-size: 13px;
    line-height: 20px;
  }

  /* ============== Tablet (≤1279px) ============== */
  @media (max-width: 1279px) {
    .account-main {
      padding-top: 86px;
    }

    .account-frame {
      padding: 32px 32px 64px;
    }

    .account-grid {
      grid-template-columns: 1fr;
      gap: 32px;
    }

    .account-aside {
      position: static;
      flex-direction: column;
      gap: 16px;
    }
  }

  /* ============== Mobile (≤767px) ============== */
  @media (max-width: 767px) {
    .account-main {
      padding-top: 74px;
      /* Чтобы bottom-nav не залезал на контент */
      padding-bottom: 76px;
    }

    .account-frame {
      padding: 20px 20px 24px;
    }

    .account-main-col {
      gap: 24px;
    }

    .account-aside {
      gap: 12px;
    }
  }
</style>
